import React from "react";
import { cn } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Typography, Switch as SwitchInput, SwitchProps } from "../../atoms";
import { ThemeColor } from "../../props";
import { FieldType } from "../../constants";
import { processErrorContent } from "./helper";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";

export interface FormFieldSwitchProps extends BaseFormFieldProps {
  /**
   * Validation constraints for the field.
   */
  constraints: FormFieldConstraint;
  /**
   * Current value of the switch (true for on, false for off).
   */
  value: boolean | null;
  /**
   * Callback function called when the switch state changes.
   */
  onChangeField: (id: string, value: boolean | null) => void;
  /**
   * Props to customize the internal Switch component.
   */
  switchProps?: SwitchProps;
  /**
   * Field type. `terms` renders the label as fine print consent text.
   */
  type?: FieldType;
}

export const Switch: React.FC<FormFieldSwitchProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched = false,
  helper = null,
  helperKey,
  constraints,
  disabled,
  keepHeight,
  noLabel,
  dense = false,
  switchProps = {},
  readOnly,
  type,
}) => {
  const error = touched && errors[0];
  const isTerms = type === FieldType.TERMS;

  const handleChange = (checked: boolean) => {
    onChangeField(id, checked);
  };

  return (
    <div className={className}>
      {/* py keeps single-line fields optically centered in the 40px row while multi-line labels stay top-aligned */}
      <div
        className={cn(
          "flex items-start gap-3 min-h-[40px]",
          dense ? "py-3" : "py-2.5",
          readOnly && "pointer-events-none",
        )}
      >
        <SwitchInput
          dense={dense}
          color={ThemeColor.primary}
          {...switchProps}
          aria-invalid={!!error}
          aria-describedby={
            helper ? `${id}-helper` : error ? `${id}-error` : undefined
          }
          id={id}
          checked={value || false}
          onChange={handleChange}
          disabled={disabled}
        />
        {!noLabel && (
          <Typography.Label
            htmlFor={id}
            content={label || { local: id }}
            color={isTerms ? ThemeColor.gray : undefined}
            className={cn(
              "select-none",
              isTerms
                ? "text-xs font-normal leading-relaxed"
                : "leading-tight",
              dense && "text-xs",
              disabled ? "cursor-not-allowed" : "cursor-pointer",
            )}
          >
            {!!constraints.presence && (
              <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
            )}
          </Typography.Label>
        )}
      </div>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "mt-1 mb-0.5" : "mt-1.5 mb-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "mt-1 mb-0.5" : "mt-1.5 mb-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
    </div>
  );
};
